import { useState, useContext } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { lddStateObject } from '../../../redux/reducer';
import { ConsentFormState, DocumentSavingPreferenceType, StateTypeContext, ConsentFormProgress, FileInfoModel } from '../types';
import { getDocumentSavingPreference, getDataReader, RPWDataReader } from '../../../livedataFuncs';
import PortalService from '../services/portal.service';
import { ActivityCenterContext } from '../../../Context/ActivityCenter';
import getArrayBufferFromFile from '../../../util/getArrayBufferFromFile';
import { prepareConsentFormDocument, uploadPreparedConsentForm, INFO_SHARING_CONSENT_FORM_CONFIG } from '../utils/consentFormWorkflow';

interface UseConsentFormConfigurationProps {
  portalId: number;
  appId: string;
  setState: React.Dispatch<React.SetStateAction<StateTypeContext>>;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseConsentFormConfigurationReturn {
  consentFormState: ConsentFormState;
  setConsentFormState: React.Dispatch<React.SetStateAction<ConsentFormState>>;
  uploadedConsentFile: File | null;
  setUploadedConsentFile: React.Dispatch<React.SetStateAction<File | null>>;
  isSubmitting: boolean;
  handleSave: () => Promise<void>;
  documentSavingPreference: DocumentSavingPreferenceType;
}

export const useConsentFormConfiguration = ({
  portalId,
  appId,
  setState,
  onSuccess,
  onError
}: UseConsentFormConfigurationProps): UseConsentFormConfigurationReturn => {
  const [consentFormState, setConsentFormState] = useState<ConsentFormState>({
    defaultConsentFormRequired: false,
    uploadConsentForm: false,
    textBoxConsentFormRequired: false,
    textBasedConsentForm: null,
    consentFormRequired: false
  });
  
  const [uploadedConsentFile, setUploadedConsentFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
  const documentSavingPreference: DocumentSavingPreferenceType = getDocumentSavingPreference(livedataRootContainer?.dataRoot);
  
  const activityCenter = useContext(ActivityCenterContext);
  const portalService = new PortalService({ appId });
  let DataReaderObj: RPWDataReader;
  DataReaderObj = getDataReader(livedataRootContainer.dataRoot);

  const sendConsentPDF = async (file: File): Promise<void> => {
    try {
      const arrayBuffer: any = await getArrayBufferFromFile(file);
      await portalService.uploadDocument(
        arrayBuffer,
        portalId.toString(),
        appId,
        INFO_SHARING_CONSENT_FORM_CONFIG.DOC_NAME_SHORT,
        INFO_SHARING_CONSENT_FORM_CONFIG.DOC_FOLDER_NAME
      );
    } catch (e) {
      throw new Error(`Error uploading consent PDF: ${e}`);
    }
  };

  const setConsentFormData = async (portalId: number): Promise<void> => {
    try {
      await portalService.setConsentFormType(consentFormState, appId, portalId);
    } catch (e) {
      throw new Error(`Error setting consent form data: ${e}`);
    }
  };

  const sendEmailNotifications = async (): Promise<void> => {
    try {
      const province = await portalService.getProvince();
      const DataReaderObj = getDataReader(livedataRootContainer?.dataRoot);
      const emailInfos: string[] = DataReaderObj.getClientContactInfo(province);

      const fileInfo = await portalService.getFileInfo();
      const isNotaryFirm = fileInfo.file?.isNotaryFirm || false;
      
      const language = DataReaderObj.getLanguage() || 'EN';

      const shortCodeData = await portalService.getShortCode(portalId);
      const shortCode = shortCodeData?.shortCode || '';

      const firmName = DataReaderObj.getFirmName() || '';

      for (const email of emailInfos) {
        const emailData = {
          appId: appId,
          from: 'noreply@ldd.ca',
          to: email,
          subject: 'ClientConnect - New Consent Form Required',
          message: 'Your lawyer has added a consent form requirement to your ClientConnect portal. Please log in to review and acknowledge.',
          ccEmail: '',
          bccEmail: '',
          user: '',
          shortCode: shortCode,
          lawyerName: firmName,
          type: 'consentForm'
        };

        try {
          await portalService.sendEmail(appId, emailData, isNotaryFirm, language);
          await portalService.logEmail(appId, emailData);
        } catch (error) {
          console.error(`Failed to notify client via email at ${email}`, error);
        }
      }
    } catch (error) {
      console.error('Error sending email notifications:', error);
      // Don't throw here - email failure shouldn't prevent consent form setup
    }
  };

  const handleSave = async (): Promise<void> => {
    setIsSubmitting(true);
    
    try {
      // Validate consent form selection
      if (!consentFormState.defaultConsentFormRequired && 
          !consentFormState.uploadConsentForm && 
          !consentFormState.textBoxConsentFormRequired) {
        throw new Error('Please select a consent form option');
      }

      if (consentFormState.defaultConsentFormRequired) {
        // Step 1: Prepare the consent form document (generate/find and convert to PDF)
        const pdfBlob = await prepareConsentFormDocument({
          appId,
          activityCenter,
          DataReaderObj,
          onProgressUpdate: (message) => {
            // Update state with progress message
            setState((prevState) => ({ 
              ...prevState, 
              page: 4, 
              progressMessage: message 
            }));
          }
        });
        
        // Step 2: Upload the prepared PDF to portal storage
        await uploadPreparedConsentForm(pdfBlob, portalId, appId, portalService);
      } else if (uploadedConsentFile && consentFormState.uploadConsentForm) {
        if (uploadedConsentFile.type !== "application/pdf") {
          throw new Error('Please upload a PDF file');
        }
        await sendConsentPDF(uploadedConsentFile);
      }

      // Step 3: Save consent form configuration to database
      await setConsentFormData(portalId);

      // Update progress: Finalizing (email notifications)
      setState((prevState) => ({ 
        ...prevState, 
        progressMessage: ConsentFormProgress.FINALIZING 
      }));

      await sendEmailNotifications();

      onSuccess?.();
      
    } catch (error) {
      console.error('Error in consent form configuration:', error);
      onError?.(error.message || 'An error occurred while configuring consent form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    consentFormState,
    setConsentFormState,
    uploadedConsentFile,
    setUploadedConsentFile,
    isSubmitting,
    handleSave,
    documentSavingPreference
  };
};
